// components/rResizableLayouts/ResizableAutoColumns.jsx
import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

/**
 * Minimal, reliable column resizer for flex layouts (content-reactive).
 * - Preserves top-level children (does NOT flatten Fragments).
 * - Normalizes arrays to child count.
 * - Resizes ref buckets & state when child count changes.
 */
const ResizableAutoColumns = forwardRef(function ResizableAutoColumns(
  {
    children,
    minPx = 100,
    maxPx = null,
    gutter = 8,
    fixedPx: fixedPxProp,
    autoUnlockOnOverflow = false,
    className = "",
    onWidthsChange,
    classPropName
  },
  ref
) {
  // Preserve top-level children (no fragment flattening)
  const cols = useMemo(() => {
    const arr = Array.isArray(children) ? children : [children];
    return arr.filter((c) => c !== null && c !== undefined && c !== false);
  }, [children]);
  const n = cols.length;

  const toArrSized = (v, fallback) => {
    if (Array.isArray(v)) {
      const out = v.slice(0, n);
      while (out.length < n) out.push(fallback);
      return out;
    }
    return Array(n).fill(v ?? fallback);
  };

  const minArr = useMemo(() => toArrSized(minPx, 100), [minPx, n]);
  const maxArr = useMemo(() => toArrSized(maxPx, null), [maxPx, n]);

  // State arrays
  const [fixedPx, setFixedPx] = useState(() =>
    fixedPxProp && fixedPxProp.length === n
      ? fixedPxProp.slice()
      : Array(n).fill(null)
  );
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [animMask, setAnimMask] = useState(() => Array(n).fill(false));
  const animTimers = useRef([]); // timers per index

  // --- Ensure ref buckets match n & keep existing ones
  const containerRef = useRef(null);
  const colRefs = useRef([]); // array of refs
  const ensureRefCount = () => {
    if (colRefs.current.length !== n) {
      const prev = colRefs.current;
      const next = Array(n);
      for (let i = 0; i < n; i++) next[i] = prev[i] ?? React.createRef();
      colRefs.current = next;
    }
  };
  ensureRefCount(); // safe to do during render

  // --- If n changes (e.g., a conditional column appears), pad/trim state arrays too
  useEffect(() => {
    setFixedPx((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) next.push(null);
      return next;
    });
    setAnimMask((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) next.push(false);
      return next;
    });
    // also clear any timers beyond n
    animTimers.current = (animTimers.current || []).slice(0, n);
  }, [n]);

  const bumpAnim = (indices) => {
    setAnimMask((prev) => {
      const next = prev.slice();
      indices.forEach((i) => {
        if (i < 0 || i >= n) return;
        next[i] = true;
        if (animTimers.current[i]) clearTimeout(animTimers.current[i]);
        animTimers.current[i] = setTimeout(() => {
          setAnimMask((p2) => {
            const n2 = p2.slice();
            n2[i] = false;
            return n2;
          });
        }, 220);
      });
      return next;
    });
  };

  const applyLayout = () => {
    for (let i = 0; i < n; i++) {
      const el = colRefs.current[i]?.current;
      if (!el) continue;
      const isFixed = fixedPx[i] != null;
      const basis = isFixed ? clamp(fixedPx[i], minArr[i], maxArr[i]) : null;
      el.style.transition =
        draggingIdx === i || animMask[i] ? "flex-basis 160ms ease" : "none";
      el.style.minWidth = "0px";
      el.style.maxWidth = maxArr[i] != null ? `${maxArr[i]}px` : "";
      if (isFixed) el.style.flex = `0 0 ${Math.max(0, basis)}px`;
      else el.style.flex = "1 1 auto";
    }
  };

  useLayoutEffect(() => {
    applyLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedPx, draggingIdx, animMask, gutter, n, minArr, maxArr]);

  useEffect(
    () => () => {
      (animTimers.current || []).forEach((t) => t && clearTimeout(t));
    },
    []
  );

  // ---- Dragging
  const dragRef = useRef(null);

  const onHandleDown = (i, e) => {
    e.preventDefault();
    e.stopPropagation();

    const colEl = colRefs.current[i]?.current;
    if (!colEl) return;
    const rect = colEl.getBoundingClientRect();

    dragRef.current = { index: i, startX: e.clientX, startWidth: rect.width };

    setDraggingIdx(i);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { passive: false });
    window.addEventListener("pointercancel", onUp, { passive: false });
    document.body.style.userSelect = "none";
  };

  const onMove = (e) => {
    e.preventDefault();
    const d = dragRef.current;
    if (!d) return;
    const i = d.index;
    const dx = e.clientX - d.startX;

    let w = d.startWidth + dx;
    w = clamp(w, minArr[i], maxArr[i]);

    const el = colRefs.current[i]?.current;
    if (el) {
      el.style.transition = "flex-basis 0ms";
      el.style.flex = `0 0 ${Math.max(0, w)}px`;
    }
  };

  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;

    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    document.body.style.userSelect = "";
    setDraggingIdx(null);

    if (!d) return;
    const i = d.index;
    const el = colRefs.current[i]?.current;
    if (!el) return;

    const finalWidth = el.getBoundingClientRect().width;
    setFixedPx((prev) => {
      const next = prev.slice();
      next[i] = clamp(finalWidth, minArr[i], maxArr[i]);
      return next;
    });

    if (onWidthsChange) {
      requestAnimationFrame(() => {
        const total = getRowUsableWidth(containerRef.current, gutter, n) || 1;
        const px = colRefs.current.map(
          (r) => r?.current?.getBoundingClientRect().width || 0
        );
        const pct = px.map((w) => (w / total) * 100);
        onWidthsChange({ px, pct });
      });
    }
  };

  // ---- Optional: auto-unlock on overflow
  useEffect(() => {
    if (!autoUnlockOnOverflow) return;

    const observers = [];
    for (let i = 0; i < n; i++) {
      const el = colRefs.current[i]?.current;
      if (!el) continue;

      const ro = new ResizeObserver(() => {
        if (fixedPx[i] == null) return;
        const overflow = el.scrollWidth > el.clientWidth + 1;
        if (overflow) {
          bumpAnim([i]);
          setFixedPx((prev) => {
            if (prev[i] == null) return prev;
            const next = prev.slice();
            next[i] = null;
            return next;
          });
        }
      });
      ro.observe(el);
      observers.push(ro);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [autoUnlockOnOverflow, fixedPx, n]);

  // ---- Imperative API
  useImperativeHandle(ref, () => ({
    setWidth(i, px) {
      if (!Number.isInteger(i) || i < 0 || i >= n) return false;
      bumpAnim([i]);
      setFixedPx((prev) => {
        const next = prev.slice();
        next[i] = clamp(px, minArr[i], maxArr[i]);
        return next;
      });
      requestAnimationFrame(() => {
        if (onWidthsChange) {
          const total = getRowUsableWidth(containerRef.current, gutter, n) || 1;
          const widths = colRefs.current.map(
            (r) => r?.current?.getBoundingClientRect().width || 0
          );
          const pct = widths.map((w) => (w / total) * 100);
          onWidthsChange({ px: widths, pct });
        }
      });
      return true;
    },
    reset(i) {
      if (!Number.isInteger(i) || i < 0 || i >= n) return false;
      bumpAnim([i]);
      setFixedPx((prev) => {
        const next = prev.slice();
        next[i] = null;
        return next;
      });
      return true;
    },
    resetAll() {
      bumpAnim([...Array(n).keys()]);
      setFixedPx(Array(n).fill(null));
      return true;
    },
    getWidths() {
      const total = getRowUsableWidth(containerRef.current, gutter, n) || 1;
      const px = colRefs.current.map(
        (r) => r?.current?.getBoundingClientRect().width || 0
      );
      const pct = px.map((w) => (w / total) * 100);
      return { px, pct, fixedPx: fixedPx.slice() };
    },
  }));

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        columnGap: 0,
        isolation: "isolate",
        height: "100%",
      }}
    >
      {cols.map((child, i) => (
        <React.Fragment key={i}>
          <div
            ref={colRefs.current[i]}
            onDoubleClick={() =>
              setFixedPx((prev) => {
                const next = prev.slice();
                next[i] = null;
                return next;
              })
            }
            style={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              // Make first paint correct (no size jump)
              minWidth: "0px",
              maxWidth: maxArr[i] != null ? `${maxArr[i]}px` : undefined,
              flex:
                fixedPx[i] != null
                  ? `0 0 ${Math.max(
                      0,
                      clamp(fixedPx[i], minArr[i], maxArr[i])
                    )}px`
                  : "1 1 auto",
            }}
          >
            {child}
          </div>

          {i < n - 1 && (
            <div
              onPointerDown={(e) => onHandleDown(i, e)}
              role="separator"
              aria-orientation="vertical"
              tabIndex={0}
              title="Drag to resize"
              style={{
                flex: "0 0 auto",
                width: `${gutter}px`,
                cursor: "col-resize",
                position: "relative",
                // zIndex: 10,
                background: "transparent",
                touchAction: "none",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  height: "30%",
                  width: "2px",
                  borderRadius: 2,
                  opacity: 0.25,
                  background: "currentColor",
                  pointerEvents: "none",
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

export default ResizableAutoColumns;

// ---- utils ----
function clamp(value, min, max) {
  const lo = min ?? -Infinity;
  const hi = max ?? +Infinity;
  return Math.max(lo, Math.min(hi, value));
}
function getRowUsableWidth(container, gutter, n) {
  if (!container) return 0;
  return container.clientWidth - gutter * (n - 1);
}
